import { useEffect, useRef, useState } from 'react';
import { Navigate, Route, Routes, Link, useLocation, useSearchParams } from 'react-router-dom';
import { Gallery } from './pages/Gallery';
import { LayoutDetailPage } from './pages/LayoutDetail';
import { DeckPage } from './pages/DeckPage';
import { SharedDeckPage } from './pages/SharedDeckPage';
import { AdminLayout } from './pages/admin/AdminLayout';
import { FamiliesPage } from './pages/admin/FamiliesPage';
import { LayoutsPage } from './pages/admin/LayoutsPage';
import { RendersPage } from './pages/admin/RendersPage';
import { BomPage } from './pages/admin/BomPage';
import { ProductsPage } from './pages/admin/ProductsPage';
import { SnapshotsPage } from './pages/admin/SnapshotsPage';
import { UsersPage } from './pages/admin/UsersPage';
import { SharesPage } from './pages/admin/SharesPage';
import { ApiKeysPage } from './pages/admin/ApiKeysPage';
import { useDeck } from './lib/deckContext';
import { AuthGate } from './components/AuthGate';
import { RoleGate } from './components/RoleGate';
import { AUTH_BYPASSED, getActiveUser, signOut } from './lib/auth/msal';
import { fetchFamilies, fetchLayouts, type Family } from './lib/api';
import { hasMinRole, useRole } from './lib/roleContext';

export default function App() {
  const location = useLocation();
  const isPublicShare = location.pathname.startsWith('/shared/');

  // The /shared/:token route is customer-facing and brings its own chrome.
  // It deliberately skips the internal Vari header AND the AuthGate so a
  // customer can view a deck without a Vari Entra account.
  if (isPublicShare) {
    return (
      <Routes>
        <Route path="/shared/:token" element={<SharedDeckPage />} />
      </Routes>
    );
  }

  return (
    <AuthGate>
      <InternalShell />
    </AuthGate>
  );
}

function InternalShell() {
  const location = useLocation();
  const isGallery = location.pathname === '/';
  const { count } = useDeck();
  const user = AUTH_BYPASSED ? null : getActiveUser();
  const { role } = useRole();
  const isAdmin = hasMinRole(role, 'design');

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      <header className="shrink-0 bg-vari-navy text-white border-b border-vari-navy/20">
        <div className="max-w-full mx-auto px-6 py-3 flex items-center gap-6">
          <Link to="/" className="shrink-0 text-lg font-semibold tracking-tight text-white">
            Vari · Idea Starter
          </Link>

          {/* Family filter chips live in the header on the gallery route so
              they're always visible while reps scroll through layouts. */}
          {isGallery && <HeaderFamilyFilters />}

          <div className="ml-auto flex items-center gap-3 shrink-0">
            {isAdmin && (
              <Link
                to="/admin"
                className="flex items-center rounded-full bg-white/10 hover:bg-white/20 px-4 py-1.5 text-sm font-medium transition-colors text-white"
              >
                Design
              </Link>
            )}
            {role === 'admin' && (
              <Link
                to="/admin/users"
                className="flex items-center rounded-full bg-white/10 hover:bg-white/20 px-4 py-1.5 text-sm font-medium transition-colors text-white"
              >
                Admin
              </Link>
            )}
            <Link
              to="/deck"
              className="flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/20 px-4 py-1.5 text-sm font-medium transition-colors text-white"
            >
              <span>Your deck</span>
              <span
                className={`min-w-6 text-center rounded-full px-2 text-xs font-semibold ${
                  count > 0 ? 'bg-vari-yellow text-vari-navy' : 'bg-white/15 text-white/70'
                }`}
              >
                {count}
              </span>
            </Link>
            {user ? (
              <div className="hidden sm:flex items-center gap-3 text-xs text-white/75">
                <span title={user.email}>
                  {user.name ?? user.email}
                  {role && role !== 'user' && (
                    <span className="ml-1.5 px-1.5 py-0.5 bg-vari-yellow/30 text-vari-yellow rounded text-xs font-medium">
                      {role}
                    </span>
                  )}
                </span>
                <button
                  type="button"
                  onClick={() => void signOut()}
                  className="text-white/60 hover:text-white underline-offset-2 hover:underline"
                >
                  Sign out
                </button>
              </div>
            ) : (
              <span className="text-xs text-white/60 hidden sm:inline">
                {AUTH_BYPASSED ? 'Internal · v0.1 · auth bypass' : 'Internal · v0.1'}
              </span>
            )}
          </div>
        </div>
      </header>

      <main className="flex-1 overflow-hidden">
        <Routes>
          <Route path="/" element={<Gallery />} />
          <Route path="/layouts/:code" element={<LayoutDetailPage />} />
          <Route path="/deck" element={<DeckPage />} />

          {/* Admin panel — design+ required at the outer gate, admin-only pages
              have an inner RoleGate to show AccessDenied for design users */}
          <Route
            path="/admin/*"
            element={
              <RoleGate require="design">
                <AdminLayout />
              </RoleGate>
            }
          >
            <Route index element={<Navigate to="/admin/layouts" replace />} />
            <Route path="layouts" element={<LayoutsPage />} />
            <Route path="layouts/:id/renders" element={<RendersPage />} />
            <Route path="layouts/:id/bom" element={<BomPage />} />
            <Route path="families" element={<FamiliesPage />} />
            <Route
              path="products"
              element={
                <RoleGate require="admin">
                  <ProductsPage />
                </RoleGate>
              }
            />
            <Route
              path="snapshots"
              element={
                <RoleGate require="admin">
                  <SnapshotsPage />
                </RoleGate>
              }
            />
            <Route
              path="users"
              element={
                <RoleGate require="admin">
                  <UsersPage />
                </RoleGate>
              }
            />
            <Route
              path="shares"
              element={
                <RoleGate require="admin">
                  <SharesPage />
                </RoleGate>
              }
            />
            <Route
              path="api-keys"
              element={
                <RoleGate require="admin">
                  <ApiKeysPage />
                </RoleGate>
              }
            />
          </Route>
        </Routes>
      </main>

      {!isGallery && !location.pathname.startsWith('/admin') && (
        <footer className="shrink-0 border-t border-vari-soft-gray bg-white">
          <div className="max-w-7xl mx-auto px-6 py-3 text-xs text-vari-gray">
            Prices effective 01/2026. For internal Vari use only.
          </div>
        </footer>
      )}
    </div>
  );
}

/** Family filter chips for the top nav bar. URL-synced via search params so
 * the gallery, header, and any bookmark all stay in sync. */
function HeaderFamilyFilters() {
  const [searchParams, setSearchParams] = useSearchParams();
  const familySlug = searchParams.get('family');
  const [families, setFamilies] = useState<Family[] | null>(null);
  const [loadingSlug, setLoadingSlug] = useState<string | null>(null);
  const { deck, addLayouts, removeLayoutsByFamily } = useDeck();

  useEffect(() => {
    let cancelled = false;
    fetchFamilies()
      .then((r) => { if (!cancelled) setFamilies(r.families); })
      .catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, []);

  if (!families) return null;

  const setFilter = (slug: string | null): void => {
    if (slug) setSearchParams({ family: slug });
    else setSearchParams({});
  };

  const handleFamilyCheckbox = async (family: Family): Promise<void> => {
    const inDeck = deck.items.filter((i) => i.familySlug === family.slug).length;
    if (inDeck > 0) {
      removeLayoutsByFamily(family.slug);
      return;
    }
    setLoadingSlug(family.slug);
    try {
      const { layouts } = await fetchLayouts(family.slug);
      addLayouts(layouts);
    } finally {
      setLoadingSlug(null);
    }
  };

  return (
    <div className="flex items-center justify-center gap-2 min-w-0 flex-1 overflow-x-auto">
      <HeaderChip active={familySlug === null} onClick={() => setFilter(null)}>
        All
      </HeaderChip>
      {families.map((f) => {
        const inDeck = deck.items.filter((i) => i.familySlug === f.slug).length;
        const allInDeck = inDeck === f.layoutCount;
        const someInDeck = inDeck > 0 && !allInDeck;
        const loading = loadingSlug === f.slug;
        return (
          <div key={f.slug} className="flex items-center gap-1 shrink-0">
            <HeaderChip active={familySlug === f.slug} onClick={() => setFilter(f.slug)}>
              {f.name}
            </HeaderChip>
            <FamilyCheckbox
              checked={allInDeck}
              indeterminate={someInDeck}
              loading={loading}
              title={allInDeck ? `Remove all ${f.name} from deck` : `Add all ${f.name} to deck`}
              onClick={() => void handleFamilyCheckbox(f)}
            />
          </div>
        );
      })}
    </div>
  );
}

function FamilyCheckbox({
  checked,
  indeterminate,
  loading,
  title,
  onClick,
}: {
  checked: boolean;
  indeterminate: boolean;
  loading: boolean;
  title: string;
  onClick: () => void;
}) {
  const ref = useRef<HTMLButtonElement>(null);

  return (
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      disabled={loading}
      title={title}
      className={`w-4 h-4 rounded border flex items-center justify-center transition-all shrink-0 ${
        checked || indeterminate
          ? 'bg-vari-yellow border-vari-yellow'
          : 'border-white/40 hover:border-white/70 bg-transparent'
      } disabled:opacity-50`}
    >
      {loading ? (
        <svg className="w-2.5 h-2.5 animate-spin text-vari-navy" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
        </svg>
      ) : checked ? (
        <svg viewBox="0 0 10 10" className="w-2.5 h-2.5 text-vari-navy" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M1.5 5l2.5 2.5 4.5-4" />
        </svg>
      ) : indeterminate ? (
        <svg viewBox="0 0 10 10" className="w-2.5 h-2.5 text-vari-navy" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
          <path d="M2 5h6" />
        </svg>
      ) : null}
    </button>
  );
}

function HeaderChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`shrink-0 rounded-full px-3 py-1 text-xs whitespace-nowrap transition-colors ${
        active
          ? 'bg-vari-yellow text-vari-navy font-medium'
          : 'bg-white/10 text-white/80 hover:bg-white/20'
      }`}
    >
      {children}
    </button>
  );
}
